import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { assets, transactions } from '@/mocks/transactions';

const statusStyle: Record<string, string> = {
  success: 'bg-emerald-500/15 text-emerald-400',
  approved: 'bg-emerald-500/15 text-emerald-400',
  pending: 'bg-accent-500/15 text-accent-400',
  rejected: 'bg-rose-500/15 text-rose-400',
};

const statusLabel: Record<string, string> = {
  success: 'Thành công',
  approved: 'Đã duyệt',
  pending: 'Chờ xử lý',
  rejected: 'Từ chối',
};

export default function Assets() {
  const totalValue = assets.reduce((sum, a) => sum + a.quantity * a.currentPrice, 0);
  const totalInvest = assets.reduce((sum, a) => sum + a.quantity * a.buyPrice, 0);
  const profit = totalValue - totalInvest;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6">Tài sản &amp; lịch sử giao dịch</h1>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
              <p className="text-xs text-foreground-600">Tổng giá trị tài sản</p>
              <p className="mt-1 text-xl md:text-2xl font-bold text-gold-gradient whitespace-nowrap">{totalValue.toLocaleString('vi-VN')}đ</p>
            </div>
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
              <p className="text-xs text-foreground-600">Tổng vốn đầu tư</p>
              <p className="mt-1 text-xl md:text-2xl font-bold text-foreground-950 whitespace-nowrap">{totalInvest.toLocaleString('vi-VN')}đ</p>
            </div>
            <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
              <p className="text-xs text-foreground-600">Lợi nhuận</p>
              <p className={`mt-1 text-xl md:text-2xl font-bold whitespace-nowrap ${profit >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                {profit >= 0 ? '+' : ''}{profit.toLocaleString('vi-VN')}đ
              </p>
            </div>
          </div>

          <h2 className="text-lg font-semibold text-foreground-950 mb-4">Danh mục vàng đang nắm giữ</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {assets.map((a) => {
              const pnl = (a.currentPrice - a.buyPrice) * a.quantity;
              return (
                <div key={a.id} className="rounded-xl border border-background-200/60 bg-background-100 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-md bg-primary-500/15 flex items-center justify-center">
                        <i className="ri-copper-coin-line text-primary-400 text-base"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-950">{a.name}</p>
                        <p className="text-xs text-foreground-600">{a.code}</p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-foreground-950 whitespace-nowrap">{a.quantity} {a.unit}</span>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-foreground-600">Giá mua</span><span className="text-foreground-900">{a.buyPrice.toLocaleString('vi-VN')}đ</span></div>
                    <div className="flex justify-between"><span className="text-foreground-600">Giá hiện tại</span><span className="text-foreground-900">{a.currentPrice.toLocaleString('vi-VN')}đ</span></div>
                    <div className="flex justify-between"><span className="text-foreground-600">Lãi/Lỗ</span><span className={`font-semibold ${pnl >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>{pnl >= 0 ? '+' : ''}{pnl.toLocaleString('vi-VN')}đ</span></div>
                  </div>
                </div>
              );
            })}
          </div>

          <h2 className="text-lg font-semibold text-foreground-950 mb-4">Lịch sử giao dịch</h2>
          <div className="overflow-x-auto rounded-xl border border-background-200/60">
            <table className="w-full text-sm min-w-[680px]">
              <thead>
                <tr className="bg-background-100 text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Loại</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Chi tiết</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600 text-right">Số tiền</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Trạng thái</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Thời gian</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.id} className="border-t border-background-200/40 hover:bg-background-100/60 transition-colors">
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                        t.type === 'buy' ? 'bg-emerald-500/15 text-emerald-400' : t.type === 'sell' ? 'bg-rose-500/15 text-rose-400' : t.type === 'deposit' ? 'bg-primary-500/15 text-primary-400' : 'bg-accent-500/15 text-accent-400'
                      }`}>{t.label}</span>
                    </td>
                    <td className="px-4 py-3 text-foreground-800">{t.detail}</td>
                    <td className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${t.amount >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {t.amount >= 0 ? '+' : ''}{t.amount.toLocaleString('vi-VN')}đ
                    </td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${statusStyle[t.status]}`}>{statusLabel[t.status]}</span></td>
                    <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{t.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex justify-center">
            <Link to="/giao-dich" className="px-6 py-3 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors">
              Giao dịch ngay
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}